import { useState } from "react";
import { Building2, Pencil, Plus, Trash2 } from "lucide-react";
import { ApiError, errorDetail } from "@/lib/api-client";
import {
  Alert, Badge, Button, Field, Input, PageHeader, Spinner, Switch,
  Table, TableBody, TableCell, TableEmpty, TableHead, TableHeader, TableRow, toast,
} from "@/components/ui";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { FormDialog } from "../components/FormDialog";
import { useCompanies, useCreateCompany, useDeleteCompany, useUpdateCompany } from "./hooks";
import type { Company } from "./api";
import { fenToYuan, parseYuanToFen } from "@/features/billing/utils";

/** 单价展示: 单独设置的显示 "¥0.10", 未设置显示 "默认 ¥0.08" */
function PriceText({ fen, defaultFen }: { fen: number | null | undefined; defaultFen: number | undefined }) {
  if (fen == null) {
    return <span className="text-muted-foreground">默认 ¥{fenToYuan(defaultFen ?? 0)}</span>;
  }
  return <span className="font-medium text-foreground">¥{fenToYuan(fen)}</span>;
}

export function CompaniesPage() {
  const { data, isLoading, error } = useCompanies();
  const createMut = useCreateCompany();
  const updateMut = useUpdateCompany();
  const deleteMut = useDeleteCompany();

  const [showCreate, setShowCreate] = useState(false);
  const [confirmDel, setConfirmDel] = useState<Company | null>(null);
  const [editPrices, setEditPrices] = useState<Company | null>(null);
  const defaultQuote = data?.default_price_quote_fen;
  const defaultImage = data?.default_price_image_fen;

  const toggle = (c: Company) => {
    const on = !c.is_active;
    updateMut.mutate(
      { id: c.id, payload: { is_active: on ? 1 : 0 } },
      {
        onSuccess: () => toast.success(on ? `已启用「${c.name}」` : `已停用「${c.name}」`),
        onError: (e) => toast.error(on ? "启用失败" : "停用失败", { description: errorDetail(e, "请稍后重试") }),
      },
    );
  };

  return (
    <div className="mx-auto max-w-6xl space-y-5 p-4 sm:p-6">
      <PageHeader
        title="公司管理"
        description="租户公司、账号数量与计费单价 (未单独设置时使用系统默认单价)"
        actions={<Button icon={<Plus />} onClick={() => setShowCreate(true)}>新建公司</Button>}
      />

      {error && <Alert>{error instanceof ApiError ? `加载失败 (${error.status})` : "加载失败"}</Alert>}

      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead numeric className="hidden w-14 sm:table-cell">ID</TableHead>
            <TableHead className="min-w-[8rem]">名称</TableHead>
            <TableHead numeric className="w-20">用户数</TableHead>
            <TableHead className="hidden min-w-[9rem] md:table-cell">计费单价</TableHead>
            <TableHead className="w-28">状态</TableHead>
            <TableHead className="hidden w-28 sm:table-cell">创建时间</TableHead>
            <TableHead className="w-24 text-right">操作</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading && (
            <tr>
              <td colSpan={7} className="py-8 text-center text-sm text-muted-foreground">
                <span className="inline-flex items-center gap-2"><Spinner /> 加载中…</span>
              </td>
            </tr>
          )}
          {!isLoading && (!data?.data || data.data.length === 0) && (
            <TableEmpty colSpan={7} icon={Building2} title="暂无公司" />
          )}
          {data?.data.map((c) => (
            <TableRow key={c.id}>
              <TableCell numeric className="hidden text-muted-foreground sm:table-cell">{c.id}</TableCell>
              <TableCell className="font-medium text-foreground">{c.name}</TableCell>
              <TableCell numeric>{c.user_count ?? "-"}</TableCell>
              <TableCell className="hidden whitespace-nowrap text-xs tabular-nums md:table-cell">
                <div>报价 <PriceText fen={c.price_quote_fen} defaultFen={c.default_price_quote_fen ?? defaultQuote} /> /次</div>
                <div>画图 <PriceText fen={c.price_image_fen} defaultFen={c.default_price_image_fen ?? defaultImage} /> /张</div>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={!!c.is_active}
                    aria-label={c.is_active ? `停用 ${c.name}` : `启用 ${c.name}`}
                    onChange={() => toggle(c)}
                  />
                  <Badge variant={c.is_active ? "success" : "neutral"} className="hidden sm:inline-flex">{c.is_active ? "启用" : "停用"}</Badge>
                </div>
              </TableCell>
              <TableCell className="hidden whitespace-nowrap text-xs tabular-nums text-muted-foreground sm:table-cell">
                {c.created_at ? c.created_at.slice(0, 10) : "-"}
              </TableCell>
              <TableCell className="whitespace-nowrap text-right">
                <Button variant="ghost" size="icon-sm" onClick={() => setEditPrices(c)} aria-label={`设置 ${c.name} 的计费单价`}
                  title="计费单价" className="text-muted-foreground hover:text-foreground">
                  <Pencil />
                </Button>
                <Button variant="ghost" size="icon-sm" onClick={() => setConfirmDel(c)} aria-label="删除" title="删除"
                  className="text-muted-foreground hover:bg-danger-soft hover:text-danger-strong">
                  <Trash2 />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {showCreate && (
        <CreateCompanyForm
          onClose={() => setShowCreate(false)}
          onSubmit={async (v) => {
            await createMut.mutateAsync(v);
            setShowCreate(false);
            toast.success(`已创建公司「${v.name}」`);
          }}
          pending={createMut.isPending}
        />
      )}

      {editPrices && (
        <PricesForm
          company={editPrices}
          defaultQuoteFen={editPrices.default_price_quote_fen ?? defaultQuote ?? 0}
          defaultImageFen={editPrices.default_price_image_fen ?? defaultImage ?? 0}
          pending={updateMut.isPending}
          onClose={() => setEditPrices(null)}
          onSubmit={async (payload) => {
            await updateMut.mutateAsync({ id: editPrices.id, payload });
            toast.success(`已更新「${editPrices.name}」的计费单价`);
            setEditPrices(null);
          }}
        />
      )}

      <ConfirmDialog
        open={!!confirmDel}
        title={`停用公司 "${confirmDel?.name ?? ""}"`}
        message="将级联停用该公司下所有用户。该操作可通过点击'状态'切回启用恢复。"
        destructive
        confirmText="停用"
        onCancel={() => setConfirmDel(null)}
        onConfirm={() => {
          if (confirmDel) {
            const name = confirmDel.name;
            deleteMut.mutate(confirmDel.id, {
              onSuccess: () => toast.success(`已停用「${name}」`),
              onError: (e) => toast.error("操作失败", { description: errorDetail(e, "请稍后重试") }),
            });
          }
          setConfirmDel(null);
        }}
      />
    </div>
  );
}

interface CreateProps {
  onClose: () => void;
  onSubmit: (v: {
    name: string;
    admin_username: string;
    admin_password: string;
    admin_display_name?: string;
  }) => Promise<void>;
  pending: boolean;
}

function CreateCompanyForm({ onClose, onSubmit, pending }: CreateProps) {
  const [name, setName] = useState("");
  const [adminUser, setAdminUser] = useState("");
  const [adminPwd, setAdminPwd] = useState("");
  const [adminDisplay, setAdminDisplay] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const handle = async () => {
    setErr(null);
    if (!name.trim() || !adminUser.trim() || adminPwd.length < 6) {
      setErr("请填写公司名 / 管理员用户名 / 至少 6 位密码");
      return;
    }
    try {
      await onSubmit({
        name: name.trim(),
        admin_username: adminUser.trim(),
        admin_password: adminPwd,
        admin_display_name: adminDisplay.trim(),
      });
    } catch (e) {
      setErr(e instanceof ApiError ? ((e.body as { detail?: string } | null)?.detail || `失败 (${e.status})`) : "失败");
    }
  };

  return (
    <FormDialog title="新建公司" description="同时创建该公司的管理员账号" pending={pending}
      submitText="创建" pendingText="创建中…" onClose={onClose} onSubmit={handle} err={err}>
      <Field label="公司名" required>
        <Input value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="管理员用户名" required>
          <Input value={adminUser} onChange={(e) => setAdminUser(e.target.value)} autoComplete="off" />
        </Field>
        <Field label="管理员密码" hint="至少 6 位" required>
          <Input type="password" value={adminPwd} onChange={(e) => setAdminPwd(e.target.value)} autoComplete="new-password" />
        </Field>
      </div>
      <Field label="管理员显示名" hint="可空">
        <Input value={adminDisplay} onChange={(e) => setAdminDisplay(e.target.value)} />
      </Field>
    </FormDialog>
  );
}

interface PricesProps {
  company: Company;
  defaultQuoteFen: number;
  defaultImageFen: number;
  pending: boolean;
  onClose: () => void;
  onSubmit: (v: { price_quote_fen: number | null; price_image_fen: number | null }) => Promise<void>;
}

const fenToInput = (fen: number | null | undefined) => (fen == null ? "" : fenToYuan(fen));

/** 超管设置某公司的计费单价: 输入元 (两位小数), 留空 = 使用系统默认 (.env) */
function PricesForm({ company, defaultQuoteFen, defaultImageFen, pending, onClose, onSubmit }: PricesProps) {
  const [quote, setQuote] = useState(() => fenToInput(company.price_quote_fen));
  const [image, setImage] = useState(() => fenToInput(company.price_image_fen));
  const [err, setErr] = useState<string | null>(null);
  const quoteFen = parseYuanToFen(quote);
  const imageFen = parseYuanToFen(image);
  const BAD = "请输入不小于 0 的金额，最多两位小数";

  const handle = async () => {
    setErr(null);
    if (quoteFen === undefined || imageFen === undefined) {
      setErr(BAD);
      return;
    }
    try {
      await onSubmit({ price_quote_fen: quoteFen, price_image_fen: imageFen });
    } catch (e) {
      setErr(errorDetail(e, "保存失败"));
    }
  };

  return (
    <FormDialog title={`计费单价 · ${company.name}`} description="单位：元。留空表示使用系统默认单价；填 0 表示免费。"
      pending={pending} submitText="保存" pendingText="保存中…" onClose={onClose} onSubmit={handle} err={err}>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="智能报价 (元/次)" hint={`留空 = 使用默认 ¥${fenToYuan(defaultQuoteFen)}`}
          error={quoteFen === undefined ? BAD : undefined}>
          <Input inputMode="decimal" value={quote} onChange={(e) => setQuote(e.target.value)}
            placeholder={`使用默认 ${fenToYuan(defaultQuoteFen)}`} autoComplete="off" />
        </Field>
        <Field label="AI 画图 (元/张)" hint={`留空 = 使用默认 ¥${fenToYuan(defaultImageFen)}`}
          error={imageFen === undefined ? BAD : undefined}>
          <Input inputMode="decimal" value={image} onChange={(e) => setImage(e.target.value)}
            placeholder={`使用默认 ${fenToYuan(defaultImageFen)}`} autoComplete="off" />
        </Field>
      </div>
    </FormDialog>
  );
}
